import type { QueueJobStatus, QueueJobView } from '@/api/wsr/types';

export type QueueTranslateFn = (
  key: string,
  fallback?: string,
  values?: Record<string, string | number>,
) => string;

const QUEUE_KIND_COPY: Record<string, { key: string; fallback: string }> = {
  GENESIS: { key: 'queue.kind.GENESIS', fallback: 'genesis' },
  BACKFILL: { key: 'queue.kind.BACKFILL', fallback: 'backfill' },
  LEADING_EDGE: { key: 'queue.kind.LEADING_EDGE', fallback: 'leading edge' },
  INCREMENTAL: { key: 'queue.kind.INCREMENTAL', fallback: 'incremental' },
};

export function queueKindValue(runKind: string | null, t: QueueTranslateFn): string {
  if (runKind === null) return '—';
  const copy = QUEUE_KIND_COPY[runKind];
  return copy ? t(copy.key, copy.fallback) : '—';
}

function lifecycleRank(status: QueueJobStatus): number {
  if (status === 'RUNNING' || status === 'CLAIMED') return 0;
  if (status === 'PENDING') return 2;
  return 1;
}

function timelineSortTime(job: QueueJobView, rank: number): number {
  if (rank === 0) return new Date(job.startedAt ?? job.scheduledFor).getTime();
  if (rank === 2) return new Date(job.scheduledFor).getTime();
  return new Date(job.finishedAt ?? job.startedAt ?? job.scheduledFor).getTime();
}

/** Shared Queue ordering: in-flight, recent terminal rows, then queued rows. */
export function compareQueueJobs(a: QueueJobView, b: QueueJobView): number {
  const rankA = lifecycleRank(a.status);
  const rankB = lifecycleRank(b.status);
  if (rankA !== rankB) return rankA - rankB;
  const timeA = timelineSortTime(a, rankA);
  const timeB = timelineSortTime(b, rankB);
  if (timeA !== timeB) return rankA === 2 ? timeA - timeB : timeB - timeA;
  return a.id - b.id;
}
